import moment from 'moment';
import { actions } from '../../context';
import DateFnsUtils from '@date-io/date-fns';
import { MenuItem } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { TimePicker, DatePicker, Select, TextField } from 'mui-rff';
import { Button, ModalFooter, Row, Col } from 'reactstrap';
import { useEffect, useState } from 'react';

const durations = [`Hour`, `Day`, `Week`, `Month`, `Year`];
const emptyGuid = '00000000-0000-0000-0000-000000000000';

function MutePracticeForm({ practiceId, currentPracticeId }) {
    const dispatch = useDispatch();
    const [muteReasons, setMuteReasons] = useState([]);

    const submit = async (vals) => {
        const payload = {
            ...vals,
            muteUntil: moment(vals.muteUntil).unix(),
        };

        dispatch(actions.practice.mutePractice(practiceId, payload))
            .then(() => {
                dispatch(actions.toggle.triggerRefresh());
                if (practiceId === currentPracticeId)
                    dispatch(actions.toggle.triggerPracticeRefresh());
            })
            .then(() =>
                dispatch(actions.toggle.toggleMutePracticeModal(false))
            );
    };

    useEffect(() => {
        dispatch(actions.muteReasons.fetchMuteReasons()).then((res) => {
            setMuteReasons(res || []);
        });
    }, [dispatch]);

    const validate = (values) => {
        const errors = {};
        if (!values.muteReasonId || values.muteReasonId === emptyGuid) {
            errors.muteReasonId = 'Required';
        }
        return errors;
    };

    return (
        <Form
            onSubmit={submit}
            initialValues={{
                muteUntil: moment().toDate(),
                interval: 0,
                frequency: 0,
                muteReasonId: emptyGuid,
            }}
            validate={validate}
            render={({ handleSubmit, form, submitting, pristine, values }) => (
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col className="px-2 my-2">
                            <div className="d-flex justify-content-center opaque p-3">
                                <div className="text-dark pt-3">Mute for</div>
                                &emsp;
                                <div className="w-25">
                                    <TextField
                                        name="interval"
                                        type="number"
                                        label="Amount"
                                        inputProps={{
                                            min: 0,
                                            style: {
                                                textAlign: 'center',
                                            },
                                        }}
                                        // TODO: fix this deprecated moment code
                                        onChangeCapture={(e) =>
                                            (values.muteUntil = moment()
                                                .add(
                                                    e.target.value,
                                                    `${durations[
                                                        values.frequency
                                                    ].toLowerCase()}`
                                                )
                                                .toDate())
                                        }
                                    />
                                </div>
                                &emsp;
                                <div className="w-25">
                                    <Select
                                        id="muteFrequencySelector"
                                        name="frequency"
                                        label="Interval"
                                        onChange={(e) => {
                                            let muteUntilDate = moment()
                                                .add(
                                                    values.interval,
                                                    `${durations[
                                                        e.target.value
                                                    ].toLowerCase()}s`
                                                )
                                                .toDate();
                                            form.change(
                                                'muteUntil',
                                                muteUntilDate
                                            );
                                            form.change(
                                                'frequency',
                                                e.target.value
                                            );
                                        }}
                                        formControlProps={{
                                            margin: 'none',
                                        }}>
                                        {durations.map((d, x) => (
                                            <MenuItem key={x} value={x}>
                                                {d}
                                                {values.interval !== 1
                                                    ? 's'
                                                    : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="px-2 my-2">
                            <div className="bg-white rounded px-2 sc-shadow">
                                <DatePicker
                                    name="muteUntil"
                                    margin="normal"
                                    minDate={moment().add(-1, 'days').toDate()}
                                    label="Mute Until Date"
                                    dateFunsUtils={DateFnsUtils}
                                />
                            </div>
                        </Col>
                        <Col className="px-2 my-2">
                            <div className="bg-white rounded px-2 sc-shadow">
                                <TimePicker
                                    name="muteUntil"
                                    margin="normal"
                                    label="Mute Until Time"
                                    dateFunsUtils={DateFnsUtils}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="px-2 my-2">
                            <label>Reason</label>
                            <div className="d-flex justify-content-center opaque p-3">
                                <Select
                                    id="muteReasonSelector"
                                    name="muteReasonId">
                                    <MenuItem
                                        key={emptyGuid}
                                        value={emptyGuid}
                                        disabled>
                                        --Select a reason category--
                                    </MenuItem>
                                    {muteReasons
                                        .sort(compareReasonName)
                                        .map((reason) => (
                                            <MenuItem
                                                key={reason.id}
                                                value={reason.id}>
                                                {reason.reason}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="px-2 my-2">
                            <label>Notes</label>
                            <Field
                                className="w-100 p-2 rounded"
                                component="textarea"
                                name="muteNote"
                                placeholder="Reason for muting"
                                rows="4"
                            />
                        </Col>
                    </Row>
                    <ModalFooter>
                        <Button
                            color="danger"
                            onClick={() =>
                                pristine
                                    ? dispatch(
                                          actions.toggle.toggleMutePracticeModal(
                                              false
                                          )
                                      )
                                    : form.reset()
                            }>
                            {pristine ? 'Cancel' : 'Reset'}
                        </Button>
                        <Button
                            color="info"
                            disabled={
                                pristine ||
                                submitting ||
                                !!form.getState().errors.muteReasonId
                            }
                            type="submit">
                            Mute
                        </Button>
                    </ModalFooter>
                </form>
            )}></Form>
    );
}

function compareReasonName(a, b) {
    if (a.reason < b.reason) {
        return -1;
    }
    if (a.reason > b.reason) {
        return 1;
    }
    return 0;
}

export default MutePracticeForm;
